import { createSelector } from 'reselect';

import { CHANNEL } from 'constants/ChatConstants';
import { FILES_EXTENSIONS_WO_SELF_SERVICE } from 'constants/ExtensionConstants';
import { STATUSES } from 'constants/InterventionConstants';
import { MODULES } from 'constants/ModuleConstants';
import { SELF_SERVICE_TYPE, SELF_SERVICE_TYPES } from 'constants/SelfServiceConstants';

import {
  getAllMessagesByInterventionId,
  getDefaultChannel as getChatDefaultChannel
} from 'modules/chat/selectors/details';
import { IntegrationTypeEnum } from 'modules/global/typing/enums/integrations.enum';
import { getName } from 'modules/guidelines/selectors/ui/dashboard/config';
import { getLocale } from 'modules/i18n/selectors/data';
import { canSaveIntervention, getInterventionDealerId, isInterventionStockVO, isReminderSelected, isSelectedBodywork, isSelectedSalesIntervention } from 'modules/interventions/selectors/details';
import { hasSelfServiceExtension, isModelFetching } from 'modules/selfServices/selectors';
import { getById as getStepById } from 'modules/steps/selectors/details';
import {
  getCurrentUserIsReadOnly,
  getCurrentUserIsStockVOManager,
  hasInterventionRole,
  isCurrentUserIsAdminOrGroupManager
} from 'modules/users/selectors/currentUser';
import { isAllowSalesLeadById, getDealerDefaultLanguage, getInfoMediariesIntegration, getDealerById, getTimeZoneOffsetByDealerId } from 'selectors/dealerSelectors';
import { getSelfServiceTagContextUtils, isSelfServiceAnswered } from 'services/InterventionUtils';
import { createDictionary } from 'utils/ObjectUtils';

import { getProps, getRoot } from 'utils/selectors';

import { isSelfServiceSentOrReplied } from '../kanban.utils';

import {
  getUIState,
  getSelectedIntervention,
  getById
} from './base';
import { isStatusBelow } from '../utils/statusUtils';

export * from './base';

export const getModals = createSelector(getUIState, ui => ui.modals);

const getSendModal = createSelector(getModals, modals => modals.send);

export const isSendModalOpen = createSelector(getSendModal, send => send.isOpen);

export const getDefaultChannelFromInterventionId = createSelector(
  getAllMessagesByInterventionId,
  getChatDefaultChannel
);

const getImportsModals = createSelector(getModals, modals => modals.imports);
const getAuditsModals = createSelector(getImportsModals, imports => imports.audit);
const getChecklistsModals = createSelector(getImportsModals, imports => imports.checklists);
const getTScansModals = createSelector(getImportsModals, imports => imports.tscan);

export const isImportAuditsModalOpen = createSelector(getAuditsModals, audit => audit.isOpen);
export const isImportChecklistsModalOpen = createSelector(getChecklistsModals, checklists => checklists.isOpen);
export const isImportTScansModalOpen = createSelector(getTScansModals, tscan => tscan.isOpen);
export const isLoadingAuditsImport = createSelector(getAuditsModals, audit => audit.isLoading);
export const isLoadingChecklistsImport = createSelector(getChecklistsModals, checklists => checklists.isLoading);
export const isLoadingTScansImport = createSelector(getTScansModals, tscan => tscan.isLoading);

export const getChecklistsImportDefaultGroupId = createSelector(
  getChecklistsModals,
  checklists => checklists.defaultChecklistGroupId || undefined
);

export const getImportedTireQuoteIds = createSelector(getSelectedIntervention, ({ checklists = [] }) =>
  checklists.reduce((acc, { originTireQuoteId }) => {
    if (originTireQuoteId) {
      acc.push(originTireQuoteId);
    }
    return acc;
  }, []));

export const getImportedTireScanIds = createSelector(getSelectedIntervention, ({ checklists = [] }) =>
  checklists.reduce((acc, { originTireScanId }) => {
    if (originTireScanId) {
      acc.push(originTireScanId);
    }
    return acc;
  }, []));

export const getImportedBodyScanIds = createSelector(getSelectedIntervention, ({ checklists = [] }) =>
  checklists.reduce((acc, { originBodyScanId }) => {
    if (originBodyScanId) {
      acc.push(originBodyScanId);
    }
    return acc;
  }, []));

export const getImportedAuditIds = createSelector(getSelectedIntervention, ({ checklists = [] }) =>
  checklists.reduce((acc, { originAuditId }) => {
    if (originAuditId) {
      acc.push(originAuditId);
    }
    return acc;
  }, []));

const getUsedConversationChannels = createSelector(
  getSelectedIntervention,
  ({ conversationContext }) => conversationContext?.channels ?? []
);

export const hasFinalInvoice = createSelector(
  getSelectedIntervention,
  getProps,
  /* you can give a finalInvoice form instance */
  ({ finalInvoice }, form) => Boolean(form ?? finalInvoice)
);

export const isSentOrMore = createSelector(
  getSelectedIntervention,
  ({ status }) => !isStatusBelow(status, STATUSES.SENT)
);

const isClosedOrMore = createSelector(getSelectedIntervention, ({ status }) => !isStatusBelow(status, STATUSES.CLOSED));

const getSelfServices = createSelector(getSelectedIntervention, intervention => intervention?.selfServices);

export const getCheckInSelfService = createSelector(getSelfServices, selfServices =>
  selfServices?.find(({ type }) => type === SELF_SERVICE_TYPE.CHECK_IN));

export const canSetCustomerInfoContact = createSelector(
  getSelectedIntervention,
  canSaveIntervention,
  isSentOrMore,
  getUsedConversationChannels,
  (intervention, canSave, isSent, channels) => {
    const hasSentSelfService = SELF_SERVICE_TYPES.some(
      type => isSelfServiceSentOrReplied(intervention ?? {}, type)
    );

    const hasSMSOrEmail = channels.some(
      channel => channel === CHANNEL.SMS || channel === CHANNEL.EMAIL
    );

    return canSave && !isSent && !hasSentSelfService && !hasSMSOrEmail;
  }
);

/** @description The home check-in is answered when any of its origin contexts reached the ANSWERED status. */
export const isCheckInAnswered = createSelector(getCheckInSelfService, checkIn => isSelfServiceAnswered(checkIn));

/**
 * @description Mirrors `canSetCustomerInfoContact`: the additional contact email/phone are locked
 * once the check-in has been answered OR the contact is validated (validated implies the check-in
 * was answered, but we keep both for robustness), while the global file lock (`canSave`) stays
 * authoritative.
 */
export const canSetAdditionalCustomerContact = createSelector(
  getSelectedIntervention,
  canSaveIntervention,
  isCheckInAnswered,
  (intervention, canSave, checkInAnswered) =>
    canSave && !checkInAnswered && !intervention?.additionalCustomer?.validated
);

/** @description This only applies to VIN and brand fields for the moment */
export const canSetVehicleInformation = createSelector(
  canSetCustomerInfoContact,
  getUsedConversationChannels,
  (canSetContactInfo, channels) => {
    const hasDriverApp = channels.some(
      channel => channel === CHANNEL.DRIVER_APP
    );
    return canSetContactInfo && !hasDriverApp;
  }
);

const fileIsClosed = createSelector(getSelectedIntervention, ({ status }) => status === STATUSES.CLOSED);

export const canSetPreApprovedWorks = createSelector(
  getCurrentUserIsReadOnly,
  getSelectedIntervention,
  hasFinalInvoice,
  fileIsClosed,
  (readOnly, { module }, hasInvoice, isClosed) =>
    !readOnly && MODULES[module].canAddPreApproved && !hasInvoice && !isClosed
);

export const canSetAdditionalWorks = createSelector(
  canSaveIntervention,
  hasFinalInvoice,
  (canSave, isEnabled) => canSave && !isEnabled
);

export const isInterventionLeadAllowed = createSelector(getRoot, getInterventionDealerId, isAllowSalesLeadById);

export const canShowInterventionLeads = createSelector(
  isInterventionLeadAllowed,
  isInterventionStockVO,
  (allowSalesLead, isStockVo) => isStockVo && allowSalesLead
);

export const canDisplayLinkedStock = createSelector(
  isInterventionLeadAllowed,
  getSelectedIntervention,
  (allowSalesLead, { lead }) => allowSalesLead && Boolean(lead) && lead.intervention.status !== STATUSES.DELETED
);

export const canRefreshDMS = createSelector(getSelectedIntervention, ({ status }) =>
  isStatusBelow(status, STATUSES.CLOSED));

export const canDisplayInfoMediaries = createSelector(
  getInfoMediariesIntegration,
  isSelectedSalesIntervention,
  isCurrentUserIsAdminOrGroupManager,
  getCurrentUserIsStockVOManager,
  (integrations, isSales, isAdminOrGM, isStockVOManager) =>
    isSales && (isAdminOrGM || isStockVOManager) && integrations.length > 0
);

export const getInterventionVehicleCheck = createSelector(
  getCheckInSelfService,
  /** @param {import('modules/selfServices/types').CheckInSelfService} checkin */
  checkin => checkin?.vehicleCheck
);

export const hasVehicleCheck = entity => entity?.items?.some?.(({ responseMedia }) => Boolean(responseMedia)) ?? false;

export const hasInterventionVehicleCheck = createSelector(getInterventionVehicleCheck, hasVehicleCheck);

export const getVehicleCheckMedias = (vehicleCheck, UILocale, locale) =>
  vehicleCheck?.items?.reduce((acc, { responseMedia, name }) => {
    if (responseMedia) {
      acc.push({
        ...responseMedia,
        name: typeof name === 'string' ? name : getName(name, UILocale, locale)
      });
    }
    return acc;
  }, []) ?? [];

export const getInterventionVehicleCheckMedias = createSelector(
  getCheckInSelfService,
  getLocale,
  getDealerDefaultLanguage,
  (checkin, UILocale, locale) => getVehicleCheckMedias(checkin?.vehicleCheck, UILocale, locale)
);

export const canAddChecklist = createSelector(
  isClosedOrMore,
  hasFinalInvoice,
  isSelectedBodywork,
  isReminderSelected,
  (isClosed, hasInvoice, isBodywork, isSelectedReminder) =>
    !isClosed && !hasInvoice && !isBodywork && !isSelectedReminder
);

export const getSelectedChecklistOriginIds = createSelector(
  getChecklistsImportDefaultGroupId,
  getSelectedIntervention,
  (checklistGroupId, { checklists = [] }) => {
    let existingChecklists = checklists;
    if (checklistGroupId) {
      existingChecklists = existingChecklists.filter(({ checklistGroupId: groupId }) => groupId === checklistGroupId);
    }
    return existingChecklists.map(({ originChecklistId }) => originChecklistId);
  }
);

export const isDelaying = createSelector(getUIState, ({ loading }) => loading.delay);

const isFollowupModelLoading = createSelector(getUIState, ({ loading }) => loading.followup);

const INTEGRATION_RULES = createDictionary(
  {
    [IntegrationTypeEnum.OPEN_FLEX]: intervention => Boolean(intervention?.vehicleInfo?.vin || intervention?.vehicleInfo?.registration)
  },
  intervention => Boolean(intervention?.vehicleInfo?.registration)
);

export const canUseIntegration = createSelector(
  canSaveIntervention,
  getSelectedIntervention,
  getProps,
  (canSave, intervention, integration) => canSave && INTEGRATION_RULES[integration](intervention)
);

export const getEditedTire = createSelector(getModals, modals => modals.editedTire ?? undefined);

export const isImportingTireReport = createSelector(getUIState, ({ loading }) => loading.importTireReport);

export const isChecklistGroupModalOpen = createSelector(
  getModals,
  ({ checklistGroupTarget }) => checklistGroupTarget.visible
);

export const isResendModalOpen = createSelector(getModals, modals => modals.resendModal);

export const isDisabledSubmitButton = createSelector(
  getCurrentUserIsReadOnly,
  isModelFetching,
  isFollowupModelLoading,
  (isReadonlyUser, isSelfServiceLoading, isFollowupLoading) =>
    isReadonlyUser || isSelfServiceLoading || isFollowupLoading
);

export const isFollowOrUnfollowLoading = createSelector(
  getUIState,
  ({ loading }) => loading.follow || loading.unfollow
);

export const getSelfServiceTagContext = createSelector(
  getSelfServices,
  hasSelfServiceExtension,
  getSelfServiceTagContextUtils
);

export const hasDealerInterventionFullAccess = createSelector(
  getDealerById,
  hasInterventionRole,
  (dealer, hasRightAccess) =>
    hasRightAccess &&
    (!dealer || FILES_EXTENSIONS_WO_SELF_SERVICE.some(extension => dealer?.extensions?.includes(extension)))
);

export const getSelectedInterventionTimeZoneOffset = createSelector(
  getRoot,
  getInterventionDealerId,
  getTimeZoneOffsetByDealerId
);

export const getRightToObjectChannels = createSelector(getById, ((data) => {
  const { objectForSms, objectForEmail } = data?.customerInfo?.rightToObject || {};
  return Boolean(objectForSms || objectForEmail);
}));

export const getClientHasMissingStepInfo = createSelector(getById, (state, _, stepId) => getStepById(state, stepId), ((data, step) => {
  const customerInfo = data?.customerInfo;
  const hasDriverApp = Boolean(step?.integrationConfig?.[IntegrationTypeEnum.MY_BMW_APP]?.externalReference);
  const vinIsMissing = hasDriverApp && !data?.vehicleInfo?.vin;
  return Boolean(vinIsMissing || !customerInfo?.email || !customerInfo?.phoneNumber?.local);
}));

export const hasRightToObjectForMarketing = createSelector(getById, ((data) => {
  const rightToObject = data?.customerInfo?.rightToObject;
  return Boolean(rightToObject?.objectForEmailMarketing || rightToObject?.objectForSMSMarketing);
}));
